import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail } from "lucide-react";

const STATUSES = ["new", "contacted", "closed"];

export default function Leads() {
    const [leads, setLeads] = useState([]);
    const [filter, setFilter] = useState("");
    const [openId, setOpenId] = useState(null);
    const [notes, setNotes] = useState({});
    const [doctor, setDoctor] = useState(null);

    async function load() {
        try {
            const url = filter ? `/me/leads?status=${filter}` : "/me/leads";
            const r = await api.get(url);
            setLeads(r.data);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        }
    }
    useEffect(() => {
        api.get("/me/doctor").then((r) => setDoctor(r.data)).catch(() => {});
        load();
    }, [filter]);

    async function updateStatus(id, status) {
        try {
            await api.put(`/me/leads/${id}`, { status });
            toast.success("Updated");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }
    async function saveNotes(id) {
        try {
            await api.put(`/me/leads/${id}`, { notes: notes[id] || "" });
            toast.success("Notes saved");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }

    const counts = {
        all: leads.length,
        new: leads.filter((l) => l.status === "new").length,
        contacted: leads.filter((l) => l.status === "contacted").length,
        closed: leads.filter((l) => l.status === "closed").length,
    };

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="leads-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Enquiries</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Leads</h1>
                <p className="text-[#17212B]/60 text-sm mt-1">Manage patient enquiries submitted through your public page.</p>
            </div>

            <div className="flex gap-2 mb-4">
                {[["", `All (${counts.all})`], ["new", `New (${counts.new})`], ["contacted", `Contacted (${counts.contacted})`], ["closed", `Closed (${counts.closed})`]].map(([v, l]) => (
                    <button
                        key={v || "all"}
                        onClick={() => setFilter(v)}
                        className={"px-3 py-1.5 rounded-full text-xs " + (filter === v ? "bg-[#123B5D] text-white" : "bg-white border border-[#123B5D]/10 text-[#17212B]/70")}
                        data-testid={`leads-tab-${v || "all"}`}
                    >{l}</button>
                ))}
            </div>

            <Card className="border-[#123B5D]/10">
                <CardContent className="pt-4">
                    {leads.length === 0 ? (
                        <div className="text-sm text-[#17212B]/60 py-8 text-center">No leads yet. Once patients submit your public page's enquiry form, they'll appear here.</div>
                    ) : (
                        <ul className="divide-y divide-[#123B5D]/10">
                            {leads.map((l) => (
                                <li key={l.id} className="py-4" data-testid={`lead-${l.id}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="flex-1">
                                            <div className="font-bold text-[#123B5D]" data-testid={`lead-name-${l.id}`}>{l.name}</div>
                                            <div className="text-xs text-[#17212B]/60 mt-0.5 flex flex-wrap gap-3">
                                                {l.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {l.phone}</span>}
                                                {l.email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {l.email}</span>}
                                                <span>{l.created_at?.slice(0, 10)}</span>
                                            </div>
                                            {l.message && <div className="text-sm text-[#17212B]/80 mt-2">{l.message}</div>}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            {STATUSES.map((s) => (
                                                <Button
                                                    key={s}
                                                    size="sm"
                                                    variant={l.status === s ? "default" : "outline"}
                                                    onClick={() => updateStatus(l.id, s)}
                                                    data-testid={`lead-status-${l.id}-${s}`}
                                                    className={l.status === s ? "bg-[#0F766E] hover:bg-[#0b5f5a]" : ""}
                                                >{s}</Button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <Textarea
                                            rows={2}
                                            placeholder="Notes (visible to you only)"
                                            value={notes[l.id] !== undefined ? notes[l.id] : (l.notes || "")}
                                            onChange={(e) => setNotes({ ...notes, [l.id]: e.target.value })}
                                            data-testid={`lead-notes-${l.id}`}
                                        />
                                        <div className="flex justify-end mt-2">
                                            <Button size="sm" variant="ghost" onClick={() => saveNotes(l.id)} data-testid={`lead-save-notes-${l.id}`}>Save notes</Button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
